import React, { useEffect, useRef, useState } from "react";
import StepsEditorSidebar, {
  StepsEditorSidebarSection as SidebarSection,
} from "~/components/shared/StepsEditorSidebar";
import FormYAMLEditorFieldLoader from "~/components/shared/FormYAMLEditorField";
import classNames from "classnames";
import Icon from "~/components/shared/Icon";

const MemoizedFormYAMLEditorField = React.memo(FormYAMLEditorFieldLoader);

const SIDEBAR_SECTION_ICONS = {
  [SidebarSection.Guide]: "heroicons/outline/book-open",
  [SidebarSection.Examples]: "heroicons/outline/code-bracket",
};

interface StepsEditorProps {
  readOnly?: boolean;
  name: string;
  value: string;
  trackingContext: {
    organizationUuid: string;
  };
  autofocus?: boolean;
  className?: string;
  children?: React.ReactNode;
  examplesPath: string;
}

/**
 * @deprecated See ~/components/pipeline/StepsEditor.tsx
 */
export function LegacyStepsEditor({
  readOnly,
  name,
  value,
  trackingContext,
  autofocus,
  className,
  children,
  examplesPath,
}: StepsEditorProps) {
  const [activeSection, setActiveSection] = useState<SidebarSection | null>(null);

  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (activeSection) {
      ref.current?.scrollIntoView({ block: "nearest" });
    }
  }, [activeSection]);

  return (
    <section
      ref={ref}
      className={classNames(
        "border-gray grid scroll-mb-4 overflow-clip rounded-sm border",
        activeSection && "sm:grid-cols-[1fr_minmax(0,320px)]",
        activeSection ? "min-h-[600px]" : "min-h-[300px]",
        className,
      )}
    >
      <div className="grid grid-rows-[auto_1fr_auto]">
        <header className="bg-silver border-gray sticky top-0 z-10 flex h-[39px] items-center justify-between border-b px-3">
          <h3 className="m-0 truncate p-0 text-sm font-medium leading-normal">YAML Steps</h3>

          {!readOnly && (
            <div className="-mr-2 hidden gap-0.5 sm:flex">
              {Object.values(SidebarSection).map((section) => {
                if (section === SidebarSection.Examples && !window.Features.PipelineExamples) {
                  return null;
                }

                return (
                  <button
                    type="button"
                    key={section}
                    onClick={() => setActiveSection(activeSection === section ? null : section)}
                    aria-pressed={activeSection === section}
                    className={classNames(
                      "flex h-7 items-center gap-1.5 rounded-md px-2 font-medium leading-none hover:bg-gray-300",
                      activeSection === section ? "!bg-purple-100 text-purple-600" : "",
                    )}
                  >
                    <Icon icon={SIDEBAR_SECTION_ICONS[section]} className="size-4" />
                    {section}
                  </button>
                );
              })}
            </div>
          )}
        </header>

        <div className="py-1">
          <MemoizedFormYAMLEditorField
            name={name}
            value={value}
            lineWrapping={true}
            readOnly={readOnly}
            autofocus={autofocus}
            updateOnValueChange={true}
          />
        </div>

        {children && (
          <footer className="border-gray sticky bottom-0 z-10 flex gap-2 border-t bg-white px-3 py-2">
            {children}
          </footer>
        )}
      </div>

      {activeSection && (
        <div className="border-gray hidden h-0 min-h-full border-l sm:block">
          <div className="sticky top-0 max-h-[min(100svh,100%)] overflow-y-auto p-3">
            <StepsEditorSidebar
              activeSection={activeSection}
              trackingContext={trackingContext}
              examplesPath={examplesPath}
            />
          </div>
        </div>
      )}
    </section>
  );
}
